/**
 * Reglas puras de la campana de notificaciones (el menú de alertas del
 * encabezado). Separadas del componente para poder probarlas sin montar React
 * y para que el archivo de la campana siga exportando solo componentes.
 *
 * La pieza sensible es `puedeVerAlerta`: es la compuerta de confidencialidad
 * entre contratistas.
 */

import type { Role } from "@constants/roles";
import { esContratista } from "@core/auth/scope";
import { mismaEmpresa } from "@/lib/empresa";

export type TipoAlerta =
  | "VENCIDA"
  | "POR_VENCER"
  | "PR_ALERTA"
  | "PR_PENDIENTE"
  | "SO_VENCIDA"
  | "SO_PENDIENTE_APROB"
  | "SO_PARA_COTIZAR"
  | "SO_APROBADA"
  | "RD_POR_REVISAR_INT"
  | "RD_POR_APROBAR_PL2"
  | "RD_DEVUELTO"
  | "SO_PENDIENTE_VOBO"
  | "RR_AJUSTE_SOLICITADO"
  | "RR_AJUSTE_AUTORIZADO"
  | "RR_AJUSTE_POR_APROBAR";

export interface Alerta {
  id: string; // <TIPO>:<entidadCodigo o id estable>
  tipo: TipoAlerta;
  label: string;
  detalle: string;
  color: string;
  fechaHora: string;
  generadoPor: string;
  dirigidoA: string; // texto descriptivo para el panel
  /** Roles autorizados a verla. Si vacío y `paraEmails` también, → solo ADMIN. */
  paraRoles?: Role[];
  /** Emails específicos autorizados a verla. */
  paraEmails?: string[];
  /**
   * Empresa dueña de la OT a la que pertenece la alerta. Cuando está presente,
   * un usuario del lado del contratista solo la ve si es de ESA empresa —
   * mismo criterio que `puedeVerSo` usa para dejarlo ver la OT.
   *
   * Se declara SIEMPRE en las alertas dirigidas a PLANNER/ADOR: si no se pudo
   * determinar la empresa, queda en cadena vacía y la alerta no se muestra a
   * ningún contratista (falla cerrado). Los roles Tecpetrol e Interventoría no
   * se ven afectados por este campo.
   */
  paraEmpresa?: string;
}

/** Usuario mínimo necesario para decidir la visibilidad de una alerta. */
export interface UsuarioAlerta {
  email: string;
  role: Role | null;
  empresa?: string;
}

/** ¿El usuario actual debe ver esta alerta? */
export function puedeVerAlerta(user: UsuarioAlerta | null, a: Alerta): boolean {
  if (!user || !user.role) return false;
  if (user.role === "ADMIN_PMIS") return true; // modo soporte: ve todo
  if (a.paraEmails?.some((e) => e.toLowerCase() === user.email.toLowerCase()))
    return true;
  if (a.paraRoles?.includes(user.role)) {
    // Confidencialidad entre contratistas: una alerta acotada a una empresa
    // solo la ve el personal de esa empresa. Sin esto, el PLANNER/ADOR de
    // cualquier contratista veía las alertas de la OT de un competidor,
    // incluidas las observaciones en texto libre de un reporte devuelto.
    if (a.paraEmpresa !== undefined && esContratista(user.role)) {
      return mismaEmpresa(user.empresa, a.paraEmpresa);
    }
    return true;
  }
  return false;
}

/**
 * Avisos del ciclo de ajuste del Registro de recursos.
 *
 * Van por la campana y no por el Panel de Alertas del Home porque el motivo del
 * ajuste es texto libre del contratista y el panel no tiene compuerta por
 * empresa: el PLANNER de un competidor leería por qué Soforesta corrigió su día.
 *
 * `interventores` son los correos de los INT de las OT de ese contratista con
 * reportes en esa fecha — el ajuste no cuelga de una sola OT, así que el aviso
 * va a todos los que revisan alguna de ellas.
 */
export function alertasAjusteRr(
  registros: {
    id: string;
    contratistaId: string;
    contratistaNombre?: string;
    fecha: string;
    ajuste?: {
      estado: string;
      motivo: string;
      solicitadoPor: string;
      solicitadoEn: string;
      autorizadoPor?: string;
      autorizadoEn?: string;
      conceptoInt?: string;
    };
  }[],
  interventores: (contratistaId: string, fecha: string) => string[] = () => [],
): Alerta[] {
  const out: Alerta[] = [];
  for (const r of registros) {
    const a = r.ajuste;
    if (!a) continue;
    const quien = r.contratistaNombre ?? r.contratistaId;
    if (a.estado === "SOLICITADO") {
      out.push({
        id: `RR_AJUSTE_SOLICITADO:${r.id}`,
        tipo: "RR_AJUSTE_SOLICITADO",
        label: `${quien} — ajuste de recursos del ${r.fecha}`,
        detalle: `Solicita corregir un día ya aprobado. Motivo: ${a.motivo}`,
        color: "#E08600",
        fechaHora: a.solicitadoEn,
        generadoPor: a.solicitadoPor,
        dirigidoA: "PL2",
        paraRoles: ["PL2", "PL", "CFE"],
      });
    }

    if (a.estado === "AUTORIZADO") {
      out.push({
        id: `RR_AJUSTE_AUTORIZADO:${r.id}`,
        tipo: "RR_AJUSTE_AUTORIZADO",
        // Es del contratista: se acota a su empresa como el resto de avisos
        // dirigidos a PLANNER/ADOR.
        paraEmpresa: r.contratistaNombre ?? r.contratistaId,
        label: `Corrección autorizada — recursos del ${r.fecha}`,
        detalle: "El PL2 autorizó el ajuste. Corrige los recursos y envía el cambio a revisión.",
        color: "#2A6FB0",
        fechaHora: a.autorizadoEn ?? a.solicitadoEn,
        generadoPor: a.autorizadoPor ?? "PL2",
        dirigidoA: quien,
        paraRoles: ["PLANNER", "ADOR"],
      });
    }

    if (a.estado === "EN_REVISION") {
      out.push({
        id: `RR_AJUSTE_POR_APROBAR:${r.id}`,
        tipo: "RR_AJUSTE_POR_APROBAR",
        label: `${quien} — ajuste de recursos por aprobar (${r.fecha})`,
        detalle: a.conceptoInt
          ? `Concepto del Interventor: ${a.conceptoInt}`
          : "Pendiente de su aprobación — el Interventor puede conceptuar, pero no es obligatorio",
        color: "#7B1FA2",
        fechaHora: a.autorizadoEn ?? a.solicitadoEn,
        generadoPor: a.solicitadoPor,
        dirigidoA: "PL2 / Interventoría",
        paraRoles: ["PL2", "PL", "CFE"],
        // El INT conceptúa sobre el cambio ya guardado; sin este aviso tendría
        // que entrar a mirar por su cuenta si hubo alguno.
        paraEmails: interventores(r.contratistaId, r.fecha),
      });
    }
  }
  return out;
}
